<script setup>
import { computed } from 'vue';

// Modal de drill-down do funil pré-atendimento.
// Lista paginada de conversas do estágio clicado, com link direto
// pra tela de conversation existente (`/app/accounts/:id/conversations/:display_id`).
const props = defineProps({
  stageLabel: { type: String, required: true },
  drill: {
    type: Object,
    required: true,
    // shape: { stage, total, page, per, items: [], loading }
  },
});

const emit = defineEmits(['close', 'changePage']);

const accountId = computed(() => {
  // Rotas autenticadas vivem em /app/accounts/:id/...
  const match = window.location.pathname.match(/\/accounts\/(\d+)/);
  return match ? match[1] : '';
});

const totalPages = computed(() => {
  const { total = 0, per = 25 } = props.drill || {};
  if (!per) return 1;
  return Math.max(1, Math.ceil(total / per));
});

const pageStart = computed(() => {
  const { page = 1, per = 25, total = 0 } = props.drill || {};
  if (!total) return 0;
  return (page - 1) * per + 1;
});
const pageEnd = computed(() => {
  const { page = 1, per = 25, total = 0 } = props.drill || {};
  return Math.min(page * per, total);
});

function conversationUrl(displayId) {
  return `/app/accounts/${accountId.value}/conversations/${displayId}`;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function statusLabel(status) {
  const map = {
    open: 'Aberta',
    resolved: 'Resolvida',
    pending: 'Pendente',
    snoozed: 'Adiada',
  };
  return map[status] || status || '-';
}

function statusClass(status) {
  const map = {
    open: 'text-n-grass-11 bg-n-grass-3',
    resolved: 'text-n-slate-11 bg-n-alpha-2',
    pending: 'text-n-amber-11 bg-n-amber-3',
    snoozed: 'text-n-brand bg-n-alpha-2',
  };
  return map[status] || 'text-n-slate-11 bg-n-alpha-2';
}

function onPrev() {
  if (props.drill.page > 1) emit('changePage', props.drill.page - 1);
}
function onNext() {
  if (props.drill.page < totalPages.value)
    emit('changePage', props.drill.page + 1);
}
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black-2 px-4"
    role="dialog"
    aria-modal="true"
    @click.self="emit('close')"
  >
    <div
      class="w-full max-w-3xl max-h-[85vh] flex flex-col rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl"
    >
      <header
        class="flex items-center justify-between gap-4 px-6 py-4 border-b border-n-weak"
      >
        <div class="min-w-0">
          <h2 class="text-heading-3 text-n-slate-12 mb-0 truncate">
            {{ stageLabel }}
          </h2>
          <p class="text-body-small text-n-slate-11 mb-0">
            {{ drill.total }} conversa{{ drill.total === 1 ? '' : 's' }} no
            recorte atual
          </p>
        </div>
        <button
          type="button"
          class="size-8 rounded-lg flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2 transition"
          aria-label="Fechar"
          @click="emit('close')"
        >
          <i class="i-lucide-x size-4" />
        </button>
      </header>

      <div class="flex-1 overflow-y-auto px-6 py-4">
        <div v-if="drill.loading" class="py-12 flex justify-center">
          <i
            class="i-lucide-loader-circle size-6 text-n-slate-11 animate-spin"
          />
        </div>

        <div
          v-else-if="!drill.items?.length"
          class="py-12 flex flex-col items-center justify-center text-center gap-2"
        >
          <span
            class="size-12 rounded-2xl bg-n-alpha-2 flex items-center justify-center"
          >
            <i class="i-lucide-inbox size-6 text-n-slate-11" />
          </span>
          <p class="text-body-main font-medium text-n-slate-12 mb-0">
            Nenhuma conversa neste estágio
          </p>
        </div>

        <table v-else class="w-full text-body-main">
          <thead
            class="text-body-small text-n-slate-11 uppercase tracking-wide"
          >
            <tr class="border-b border-n-weak">
              <th class="text-left py-2 font-medium">#</th>
              <th class="text-left py-2 font-medium">Contato</th>
              <th class="text-left py-2 font-medium">Caixa</th>
              <th class="text-left py-2 font-medium">Status</th>
              <th class="text-right py-2 font-medium">Criada em</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="c in drill.items"
              :key="c.id"
              class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 transition"
            >
              <td class="py-2">
                <a
                  :href="conversationUrl(c.display_id)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-n-brand hover:underline font-medium"
                >
                  #{{ c.display_id }}
                </a>
              </td>
              <td class="py-2 text-n-slate-12 truncate max-w-[14rem]">
                {{ c.contact_name || '-' }}
              </td>
              <td class="py-2 text-n-slate-11 truncate max-w-[10rem]">
                {{ c.inbox_name || '-' }}
              </td>
              <td class="py-2">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-label-small"
                  :class="statusClass(c.status)"
                >
                  {{ statusLabel(c.status) }}
                </span>
              </td>
              <td class="py-2 text-right text-n-slate-11">
                {{ formatDate(c.created_at) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer
        v-if="drill.total > 0"
        class="flex items-center justify-between gap-4 px-6 py-3 border-t border-n-weak"
      >
        <p class="text-body-small text-n-slate-11 mb-0">
          {{ pageStart }}-{{ pageEnd }} de {{ drill.total }}
        </p>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="px-3 py-1.5 rounded-lg border border-n-weak text-body-small text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-40 disabled:cursor-not-allowed transition"
            :disabled="drill.page <= 1 || drill.loading"
            @click="onPrev"
          >
            Anterior
          </button>
          <span class="text-body-small text-n-slate-11">
            Página {{ drill.page }} / {{ totalPages }}
          </span>
          <button
            type="button"
            class="px-3 py-1.5 rounded-lg border border-n-weak text-body-small text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-40 disabled:cursor-not-allowed transition"
            :disabled="drill.page >= totalPages || drill.loading"
            @click="onNext"
          >
            Próxima
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>
